import React from "react";
import {
  Server,
  Shield,
  Zap,
  ShoppingCart,
  Globe,
  Brush,
  ArrowRight,
  Gamepad,
  Clock,
  Headset,
} from "lucide-react";

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="group bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 relative overflow-hidden">
    {/* Hover Gradient */}
    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-xl" />
    
    {/* Glow Effect */}
    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/0 to-purple-500/0 group-hover:from-blue-500/10 group-hover:via-purple-500/10 group-hover:to-blue-500/10 blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100" />

    <div className="relative">
      {/* Icon */}
      <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-2.5 mb-4 transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
        <Icon className="w-full h-full text-blue-400 group-hover:text-blue-300 transition-colors" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-500">
        {title}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

// Main Hero Section
export default function HeroSection() {
  return (
    <section className="relative min-h-screen py-16 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gray-900">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(25,33,52,0.8),rgba(13,17,23,1))]" />

        {/* Dot Pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("/images/panel-bg.svg")`,
            backgroundPosition: "center center",
          }}
        />

        {/* Animated Glows */}
        <div className="absolute top-0 -left-1/4 w-1/2 h-1/2 bg-blue-500/20 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 -right-1/4 w-1/2 h-1/2 bg-purple-500/20 rounded-full filter blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-gradient-to-r from-gray-800/50 via-gray-800/30 to-gray-900/50 rounded-full p-1 pr-4 backdrop-blur-sm border border-gray-700/50 group hover:border-blue-500/50 transition-all duration-500">
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full px-3 py-1 text-sm font-medium text-white mr-2">
              New
            </span>
            <div className="flex items-center space-x-2">
              <Gamepad size={16} className="text-blue-400" />
              <span className="text-gray-300 text-sm">
                North America Servers
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
            Power Your Gaming World
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto font-medium leading-relaxed">
            Deploy high-performance game servers with enterprise-grade hardware
            and instant setup in just a few clicks
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <a
              href="/games"
              className="group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl transition-all hover:scale-105 duration-500 font-medium flex items-center shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
            >
              <ShoppingCart size={20} className="mr-2" />
              Discover Plans
              <ArrowRight
                size={16}
                className="ml-2 transform transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="https://discord.gg/F9wQ6XwR9g"
              className="group bg-gray-800/50 hover:bg-gray-800 text-white px-8 py-4 rounded-xl transition-all hover:scale-105 duration-500 font-medium backdrop-blur-sm border border-gray-700/50 hover:border-blue-500/50 flex items-center"
            >
              <Headset size={20} className="mr-2" />
              Contact Sales
              <ArrowRight
                size={16}
                className="ml-2 transform transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard
            icon={Server}
            title="High Performance"
            description="Powered by latest-gen hardware and optimized infrastructure for maximum speed."
          />
          <FeatureCard
            icon={Shield}
            title="99.9% Uptime"
            description="Enterprise-grade infrastructure ensuring your services to stay online."
          />
          <FeatureCard
            icon={Zap}
            title="Instant Deployment"
            description="Get your game server up and running in minutes with our automated provisioning."
          />
          <FeatureCard
            icon={Brush}
            title="Customizable Resources"
            description="Configure your resources to fit your needs, with optional upgrades."
          />
          <FeatureCard
            icon={Globe}
            title="Global Network"
            description="Multiple locations across asia and europe for optimal latency and performance."
          />
          <FeatureCard
            icon={Clock}
            title="24/7 Active Support"
            description="Expert technical support available for help around the clock."
          />
        </div>
      </div>
    </section>
  );
}
